import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConnectionError from "../../../components/ConnectionError/ConnectionError";
import EmptyState from "../../../components/EmptyState/EmptyState";
import { StatsCards } from "../../../components/common/StatsCards/StatsCards";
import { Modal } from "../../../components/common/Modal/Modal";
import { ConfirmationModal } from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { useScrollPagination } from "../../../hooks/useScrollPagination";
import { useShowScrollTop } from "../../../hooks/useShowScrollTop";
import { adminService } from "../../../services/adminService";
import { publicContentService } from "../../../services/publicContentService";
import { getErrorMessage } from "../../../utils/errors";
import {
  BOOKING_STATUSES,
  formatBookingDateTimeRange,
  getBookingStatusLabel,
} from "../../../utils/bookings";
import type {
  BookingRecord,
  BookingStatus,
  BookingStatisticsResponse,
  BookingUpdateRequestData,
  ConsultationServiceItem,
} from "../../../types";
import { BOOKING_TIMEZONE } from "../../../types";
import { AvailabilityModal } from "../../../components/booking/AvailabilityModal/AvailabilityModal";
import DateTimePicker from "../../../components/common/DateTimePicker/DateTimePicker";
import "./BookingsPage.css";

interface BookingFormState {
  serviceId: string;
  customerName: string;
  email: string;
  phone: string;
  company: string;
  startAt: string;
  endAt: string;
  timezone: string;
  notes: string;
}

const emptyBookingForm: BookingFormState = {
  serviceId: "",
  customerName: "",
  email: "",
  phone: "",
  company: "",
  startAt: "",
  endAt: "",
  timezone: BOOKING_TIMEZONE,
  notes: "",
};

const pad2 = (n: number) => String(n).padStart(2, "0");

/** ISO string → value for a datetime-local input (browser local time). */
const isoToLocalInput = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** datetime-local input value → ISO string. */
const localInputToIso = (value: string): string => new Date(value).toISOString();

function BookingsPage() {
  const { t, i18n } = useTranslation();
  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [stats, setStats] = useState<BookingStatisticsResponse | null>(null);

  // Search & Filters State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [serviceNameFilter, setServiceNameFilter] = useState("");

  // Pagination & Loading State
  // initialLoading = true only on the very first mount (shows full-page spinner).
  // isFetching     = true on every search/filter re-fetch (silent — never unmounts the UI).
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  // Consultation services from the API (filter dropdown + booking form)
  const [consultationServices, setConsultationServices] = useState<ConsultationServiceItem[]>([]);

  // Booking Form Modal State (shared for create/edit)
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<BookingRecord | null>(null);
  const [bookingForm, setBookingForm] = useState<BookingFormState>(emptyBookingForm);
  const [savingBooking, setSavingBooking] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<BookingRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Floating Go to Top hook
  const { showScrollTop, scrollToTop } = useShowScrollTop(300);

  const isArabic = i18n.resolvedLanguage?.startsWith("ar") ?? false;

  // Localized service options for the filter dropdown and the booking form
  const services = useMemo(
    () =>
      consultationServices.map((srv) => ({
        id: srv.id,
        title: isArabic ? srv.titleAr : srv.titleEn,
        durationMinutes: srv.durationMinutes,
      })),
    [consultationServices, isArabic],
  );

  // Load consultation services once on mount
  useEffect(() => {
    publicContentService
      .getConsultationServices()
      .then(setConsultationServices)
      .catch(async (e) => {
        console.error("Failed to load services", e);
        toast.error(
          await getErrorMessage(e, t("admin.servicesLoadFailed", "Failed to load consultation services")),
        );
      });
  }, [t]);

  // Smart debounce: 700ms — enough time to finish typing a full phone number.
  // isSearching is derived purely by comparing the two strings:
  // on mount both are '' so the spinner never fires on refresh.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 700);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load stats counts (independent of filters)
  const loadStats = useCallback(async () => {
    try {
      const data = await adminService.getBookingStatistics();
      setStats(data);
    } catch (e) {
      console.error("Failed to load booking stats", e);
    }
  }, []);

  // Initial / reset load
  const loadBookings = useCallback(async () => {
    // Only show the full-page spinner on the very first load.
    // For search/filter re-fetches we use isFetching so the UI stays mounted
    // and the user can keep typing without interruption.
    setIsFetching(true);
    setError(false);
    try {
      const response = await adminService.getBookings(
        debouncedSearch,
        statusFilter,
        serviceNameFilter,
        0,
        10,
      );
      setBookings(response.content);
      setPage(0);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch {
      setError(true);
    } finally {
      setIsFetching(false);
      setInitialLoading(false);
    }
  }, [debouncedSearch, statusFilter, serviceNameFilter]);

  // Load More (Infinite Scroll)
  const loadMoreBookings = useCallback(async () => {
    if (isLoadingMore || page >= totalPages - 1) return;
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await adminService.getBookings(
        debouncedSearch,
        statusFilter,
        serviceNameFilter,
        nextPage,
        10,
      );
      setBookings((prev) => [...prev, ...response.content]);
      setPage(nextPage);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch {
      toast.error(t("errors.loadFailed") || "Failed to load more bookings");
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    page,
    totalPages,
    isLoadingMore,
    debouncedSearch,
    statusFilter,
    serviceNameFilter,
    t,
  ]);

  // Re-fetch when debounced search or filters change
  useEffect(() => {
    loadBookings();
  }, [debouncedSearch, statusFilter, serviceNameFilter, loadBookings]);

  // Load stats once on mount
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Infinite scroll hook
  useScrollPagination({
    loadMore: loadMoreBookings,
    hasMore: page < totalPages - 1,
    isLoading: initialLoading || isFetching || isLoadingMore,
    threshold: 200,
  });

  const handleStatusChange = async (
    bookingId: string,
    newStatus: BookingStatus,
  ) => {
    const currentBooking = bookings.find((b) => b.id === bookingId);
    if (!currentBooking || currentBooking.status === newStatus) return;

    setSavingId(bookingId);
    try {
      const updated = await adminService.updateBookingStatus(
        bookingId,
        newStatus,
      );
      setBookings((current) =>
        current.map((b) => (b.id === bookingId ? updated : b)),
      );
      toast.success(t("admin.bookingUpdated"));
      loadStats();
    } catch (updateError) {
      toast.error(await getErrorMessage(updateError, t("admin.updateFailed")));
    }
    setSavingId(null);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
  };

  // ─── Booking Create / Edit ───

  const selectedFormService = services.find(
    (srv) => srv.id === bookingForm.serviceId,
  );
  // endAt is auto-computed when the selected service has a duration
  const endAtAutoComputed =
    selectedFormService?.durationMinutes !== undefined &&
    selectedFormService?.durationMinutes !== null;

  const openCreateBookingModal = () => {
    setEditingBooking(null);
    setBookingForm(emptyBookingForm);
    setBookingModalOpen(true);
  };

  const openEditBookingModal = (booking: BookingRecord) => {
    setEditingBooking(booking);
    setBookingForm({
      serviceId: booking.serviceId,
      customerName: booking.customerName,
      email: booking.email,
      phone: booking.phone,
      company: booking.company || "",
      startAt: isoToLocalInput(booking.startAt),
      endAt: isoToLocalInput(booking.endAt),
      timezone: booking.timezone || BOOKING_TIMEZONE,
      notes: booking.notes || "",
    });
    setBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    if (savingBooking) return;
    setBookingModalOpen(false);
    setEditingBooking(null);
  };

  const handleBookingFieldChange =
    (field: keyof BookingFormState) =>
    (
      event: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      setBookingForm((current) => ({ ...current, [field]: event.target.value }));
    };

  const resolveEndAtIso = (): string => {
    if (endAtAutoComputed && bookingForm.startAt) {
      const start = new Date(bookingForm.startAt).getTime();
      return new Date(
        start + (selectedFormService?.durationMinutes ?? 0) * 60000,
      ).toISOString();
    }
    return localInputToIso(bookingForm.endAt);
  };

  const handleBookingSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (savingBooking) return;
    if (!bookingForm.serviceId || !bookingForm.startAt) return;
    if (!endAtAutoComputed && !bookingForm.endAt) return;

    setSavingBooking(true);
    try {
      const startAtIso = localInputToIso(bookingForm.startAt);
      const endAtIso = resolveEndAtIso();
      const serviceName = selectedFormService?.title || "";

      if (editingBooking) {
        // Partial update — only send changed fields
        const changes: BookingUpdateRequestData = {};
        if (bookingForm.serviceId !== editingBooking.serviceId) {
          changes.serviceId = bookingForm.serviceId;
          changes.serviceName = serviceName;
        }
        if (bookingForm.customerName.trim() !== editingBooking.customerName)
          changes.customerName = bookingForm.customerName.trim();
        if (bookingForm.email.trim() !== editingBooking.email)
          changes.email = bookingForm.email.trim();
        if (bookingForm.phone.trim() !== editingBooking.phone)
          changes.phone = bookingForm.phone.trim();
        if (bookingForm.company.trim() !== (editingBooking.company || ""))
          changes.company = bookingForm.company.trim();
        if (startAtIso !== editingBooking.startAt) changes.startAt = startAtIso;
        if (endAtIso !== editingBooking.endAt) changes.endAt = endAtIso;
        if (
          (bookingForm.timezone.trim() || BOOKING_TIMEZONE) !==
          editingBooking.timezone
        )
          changes.timezone = bookingForm.timezone.trim() || BOOKING_TIMEZONE;
        if (bookingForm.notes.trim() !== (editingBooking.notes || ""))
          changes.notes = bookingForm.notes.trim();

        await adminService.updateBooking(editingBooking.id, changes);
        toast.success(t("admin.bookingUpdated"));
      } else {
        await adminService.createBooking({
          serviceId: bookingForm.serviceId,
          serviceName,
          customerName: bookingForm.customerName.trim(),
          email: bookingForm.email.trim(),
          phone: bookingForm.phone.trim(),
          company: bookingForm.company.trim() || undefined,
          startAt: startAtIso,
          endAt: endAtIso,
          timezone: bookingForm.timezone.trim() || BOOKING_TIMEZONE,
          calendarProvider: "GOOGLE",
          locale: isArabic ? "ar" : "en",
          notes: bookingForm.notes.trim() || undefined,
        });
        toast.success(t("admin.bookingCreated"));
      }
      setBookingModalOpen(false);
      setEditingBooking(null);
      loadBookings();
      loadStats();
    } catch (submitError) {
      toast.error(await getErrorMessage(submitError, t("admin.saveFailed")));
    } finally {
      setSavingBooking(false);
    }
  };

  // ─── Booking Delete ───

  const handleDeleteClick = (booking: BookingRecord) => {
    setBookingToDelete(booking);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!bookingToDelete || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteBooking(bookingToDelete.id);
      toast.success(t("admin.bookingDeleted"));
      setBookings((prev) => prev.filter((b) => b.id !== bookingToDelete.id));
      setTotalElements((prev) => Math.max(0, prev - 1));
      setDeleteModalOpen(false);
      setBookingToDelete(null);
      loadStats();
    } catch (deleteError) {
      toast.error(await getErrorMessage(deleteError, t("admin.deleteFailed")));
    } finally {
      setDeleting(false);
    }
  };

  // Stats cards data
  const statItems = [
    {
      title: t("admin.bookingsStat", "Total Bookings"),
      value: stats?.total ?? 0,
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M8 2v4" />
          <path d="M16 2v4" />
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M3 10h18" />
        </svg>
      ),
      iconColorClass: "stat-color-blue",
    },
    {
      title: t("admin.pendingRequests", "Pending Requests"),
      value: stats?.pending ?? 0,
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ),
      iconColorClass: "stat-color-amber",
    },
    {
      title: t("admin.confirmedMeetings", "Confirmed Meetings"),
      value: stats?.confirmed ?? 0,
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      iconColorClass: "stat-color-green",
    },
    {
      title: t("admin.cancelledBookings", "Cancelled"),
      value: stats?.cancelled ?? 0,
      icon: (
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      ),
      iconColorClass: "stat-color-brand",
    },
  ];

  // Only truthy when the user has typed something that hasn't been debounced yet.
  // Both values are '' on first render, so the spinner never shows on refresh.
  const isSearching = searchTerm !== debouncedSearch;

  // Only block the whole page on the true initial mount.
  if (initialLoading) {
    return (
      <div className="admin-loading">
        <span className="auth-spinner auth-spinner-brand"></span>
      </div>
    );
  }

  if (error) {
    return <ConnectionError onRetry={loadBookings} />;
  }

  return (
    <div className="admin-bookings-page">
      {/* Page Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">{t("admin.bookings")}</h1>
          <p className="admin-page-subtitle">{t("admin.bookingsDesc")}</p>
        </div>
        <div className="bk-header-actions">
          <button
            type="button"
            className="bk-new-booking-btn"
            onClick={openCreateBookingModal}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t("admin.newBooking", "New Booking")}
          </button>
          <button
            type="button"
            className="bk-manage-avail-btn"
            onClick={() => setAvailabilityModalOpen(true)}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {t("admin.manageAvailability", "Manage Availability")}
          </button>
        </div>
      </div>

      {/* Availability Management Modal */}
      <AvailabilityModal
        isOpen={availabilityModalOpen}
        onClose={() => setAvailabilityModalOpen(false)}
      />

      {/* Stats Cards */}
      <StatsCards items={statItems} />

      {/* ─── Filter Toolbar ─── */}
      <div className="bk-toolbar-wrap">
        <div className="bk-toolbar">
          {/* Search */}
          <div className="bk-search-field">
            <span className="bk-search-field__icon">
              {isSearching ? (
                <span className="bk-search-spinner" />
              ) : (
                <svg
                  viewBox="0 0 24 24"
                  width="17"
                  height="17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </span>
            <input
              type="text"
              className="bk-search-field__input"
              placeholder={t("admin.searchBookings")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                className="bk-search-field__clear"
                onClick={handleClearSearch}
                aria-label="Clear search"
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* Divider */}
          <div className="bk-toolbar__divider" />

          {/* Status chips */}
          <div className="bk-chip-group">
            <button
              className={`bk-chip ${statusFilter === "" ? "bk-chip--active" : ""}`}
              onClick={() => setStatusFilter("")}
              type="button"
            >
              {t("admin.allStatuses", "All")}
            </button>
            <button
              className={`bk-chip bk-chip--pending ${statusFilter === "REQUESTED" ? "bk-chip--active" : ""}`}
              onClick={() =>
                setStatusFilter(statusFilter === "REQUESTED" ? "" : "REQUESTED")
              }
              type="button"
            >
              <span className="bk-chip__dot" />
              {getBookingStatusLabel(t, "REQUESTED")}
            </button>
            <button
              className={`bk-chip bk-chip--confirmed ${statusFilter === "CONFIRMED" ? "bk-chip--active" : ""}`}
              onClick={() =>
                setStatusFilter(statusFilter === "CONFIRMED" ? "" : "CONFIRMED")
              }
              type="button"
            >
              <span className="bk-chip__dot" />
              {getBookingStatusLabel(t, "CONFIRMED")}
            </button>
            <button
              className={`bk-chip bk-chip--cancelled ${statusFilter === "CANCELLED" ? "bk-chip--active" : ""}`}
              onClick={() =>
                setStatusFilter(statusFilter === "CANCELLED" ? "" : "CANCELLED")
              }
              type="button"
            >
              <span className="bk-chip__dot" />
              {getBookingStatusLabel(t, "CANCELLED")}
            </button>
          </div>

          {/* Service select */}
          {services.length > 0 && (
            <>
              <div className="bk-toolbar__divider" />
              <div className="bk-select-wrapper">
                <svg
                  className="bk-select-wrapper__icon"
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 6h18M7 12h10M11 18h2" />
                </svg>
                <select
                  className="bk-select"
                  value={serviceNameFilter}
                  onChange={(e) => setServiceNameFilter(e.target.value)}
                  aria-label={t("admin.serviceFilter")}
                >
                  <option value="">
                    {t("admin.allServices", "All Services")}
                  </option>
                  {services.map((srv) => (
                    <option key={srv.id} value={srv.title}>
                      {srv.title}
                    </option>
                  ))}
                </select>
                <svg
                  className="bk-select-wrapper__caret"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </>
          )}
        </div>
        {/* Thin progress bar — visible only during silent re-fetches */}
        <div
          className={`bk-fetch-bar ${isFetching ? "bk-fetch-bar--active" : ""}`}
        />
      </div>

      {/* ─── Booking Cards ─── */}
      {isFetching && bookings.length === 0 ? (
        <div className="admin-loading">
          <span className="auth-spinner auth-spinner-brand" />
        </div>
      ) : bookings.length === 0 ? (
        <EmptyState
          icon={
            <svg
              viewBox="0 0 24 24"
              width="40"
              height="40"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M3 10h18" />
            </svg>
          }
          title={t("admin.noBookings")}
          description={t("admin.noBookingsDesc")}
        />
      ) : (
        <div className="bk-list">
          {bookings.map((booking) => (
            <article
              key={booking.id}
              className={`bk-card bk-card--${booking.status.toLowerCase()}`}
            >
              {/* ── Card Top Row: avatar + info + status control ── */}
              <div className="bk-card__top">
                {/* Avatar */}
                <div
                  className={`bk-avatar bk-avatar--${booking.status.toLowerCase()}`}
                >
                  {booking.customerName.charAt(0).toUpperCase()}
                </div>

                {/* Customer info */}
                <div className="bk-card__info">
                  <div className="bk-card__name-row">
                    <span className="bk-card__name">
                      {booking.customerName}
                    </span>
                    {booking.company && (
                      <span className="bk-card__company">
                        <svg
                          viewBox="0 0 24 24"
                          width="12"
                          height="12"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="2" y="7" width="20" height="14" rx="2" />
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                        </svg>
                        {booking.company}
                      </span>
                    )}
                  </div>
                  <div className="bk-card__contact-row">
                    <a
                      href={`mailto:${booking.email}`}
                      className="bk-contact-chip"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="12"
                        height="12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      {booking.email}
                    </a>
                    <a
                      href={`tel:${booking.phone}`}
                      className="bk-contact-chip"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="12"
                        height="12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      {booking.phone}
                    </a>
                  </div>
                </div>

                {/* Status control — top right */}
                <div className="bk-card__status-block">
                  <span
                    className={`bk-status-badge bk-status-badge--${booking.status.toLowerCase()}`}
                  >
                    <span className="bk-status-badge__dot" />
                    {getBookingStatusLabel(t, booking.status)}
                  </span>
                  <select
                    className="bk-status-select"
                    value={booking.status}
                    onChange={(e) =>
                      handleStatusChange(
                        booking.id,
                        e.target.value as BookingStatus,
                      )
                    }
                    disabled={savingId === booking.id}
                    aria-label="Change status"
                  >
                    {BOOKING_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {getBookingStatusLabel(t, s)}
                      </option>
                    ))}
                  </select>
                  {savingId === booking.id && (
                    <span className="bk-saving-spinner" />
                  )}
                  <div className="bk-card__crud-actions">
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn--edit"
                      onClick={() => openEditBookingModal(booking)}
                      title={t("admin.edit")}
                      aria-label={t("admin.edit")}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="bk-icon-btn bk-icon-btn--delete"
                      onClick={() => handleDeleteClick(booking)}
                      title={t("admin.delete")}
                      aria-label={t("admin.delete")}
                    >
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Divider ── */}
              <div className="bk-card__divider" />

              {/* ── Card Bottom Row: service tag + schedule + meeting link ── */}
              <div className="bk-card__bottom">
                {/* Service tag */}
                <div className="bk-service-tag">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 2L2 7l10 5 10-5-10-5z" />
                    <path d="M2 17l10 5 10-5" />
                    <path d="M2 12l10 5 10-5" />
                  </svg>
                  {booking.serviceName}
                </div>

                {/* Schedule */}
                <div className="bk-schedule">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span className="bk-schedule__time">
                    {formatBookingDateTimeRange(
                      booking.startAt,
                      booking.endAt,
                      i18n.resolvedLanguage,
                    )}
                  </span>
                  <span className="bk-schedule__tz">{booking.timezone}</span>
                </div>

                {/* Meeting link */}
                {booking.meetingLink ? (
                  <a
                    href={booking.meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bk-join-btn"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 7l-7 5 7 5V7z" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                    {t("admin.joinMeeting", "Join Meeting")}
                  </a>
                ) : (
                  <span className="bk-no-meeting">
                    {t("admin.noMeetingLink", "No link")}
                  </span>
                )}
              </div>

              {/* ── Notes ── */}
              {booking.notes && (
                <div className="bk-card__notes">
                  <svg
                    viewBox="0 0 24 24"
                    width="13"
                    height="13"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <p className="bk-card__notes-text">{booking.notes}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {/* Infinite scroll loader */}
      {isLoadingMore && (
        <div className="bk-load-more">
          <span className="auth-spinner auth-spinner-brand" />
        </div>
      )}

      {/* Create / Edit Booking Modal */}
      <Modal
        isOpen={bookingModalOpen}
        onClose={closeBookingModal}
        title={editingBooking ? t("admin.editBooking") : t("admin.newBooking")}
        className="bk-form-modal"
      >
        <form className="bk-form" onSubmit={handleBookingSubmit}>
          <div className="bk-form__field">
            <label className="bk-form__label">{t("admin.serviceFilter")}</label>
            <select
              className="bk-form__input"
              value={bookingForm.serviceId}
              onChange={handleBookingFieldChange("serviceId")}
              required
            >
              <option value="" disabled>
                {t("admin.selectService")}
              </option>
              {services.map((srv) => (
                <option key={srv.id} value={srv.id}>
                  {srv.title}
                </option>
              ))}
            </select>
          </div>

          <div className="bk-form__row">
            <div className="bk-form__field">
              <label className="bk-form__label">{t("booking.form.name")}</label>
              <input
                type="text"
                className="bk-form__input"
                value={bookingForm.customerName}
                onChange={handleBookingFieldChange("customerName")}
                required
                maxLength={120}
              />
            </div>
            <div className="bk-form__field">
              <label className="bk-form__label">{t("booking.form.email")}</label>
              <input
                type="email"
                className="bk-form__input"
                value={bookingForm.email}
                onChange={handleBookingFieldChange("email")}
                required
                maxLength={160}
              />
            </div>
          </div>

          <div className="bk-form__row">
            <div className="bk-form__field">
              <label className="bk-form__label">{t("booking.form.phone")}</label>
              <input
                type="text"
                className="bk-form__input"
                value={bookingForm.phone}
                onChange={handleBookingFieldChange("phone")}
                required
                maxLength={40}
              />
            </div>
            <div className="bk-form__field">
              <label className="bk-form__label">{t("admin.bookingCompany")}</label>
              <input
                type="text"
                className="bk-form__input"
                value={bookingForm.company}
                onChange={handleBookingFieldChange("company")}
                maxLength={120}
              />
            </div>
          </div>

          <div className="bk-form__row">
            <div className="bk-form__field">
              <label className="bk-form__label">{t("admin.startAt")}</label>
              <DateTimePicker
                value={bookingForm.startAt}
                onChange={(v) => setBookingForm((c) => ({ ...c, startAt: v }))}
                required
              />
            </div>
            {endAtAutoComputed ? (
              <div className="bk-form__field">
                <label className="bk-form__label">{t("admin.endAt")}</label>
                <div className="bk-form__computed-end">
                  {bookingForm.startAt
                    ? new Date(resolveEndAtIso()).toLocaleString(i18n.resolvedLanguage, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : t("admin.endAtAutoHint", "Auto from service duration")}
                </div>
              </div>
            ) : (
              <div className="bk-form__field">
                <label className="bk-form__label">{t("admin.endAt")}</label>
                <DateTimePicker
                  value={bookingForm.endAt}
                  onChange={(v) => setBookingForm((c) => ({ ...c, endAt: v }))}
                  required
                />
              </div>
            )}
            <div className="bk-form__field">
              <label className="bk-form__label">{t("admin.bookingTimezone")}</label>
              <input
                type="text"
                className="bk-form__input"
                value={bookingForm.timezone}
                onChange={handleBookingFieldChange("timezone")}
                required
                maxLength={80}
              />
            </div>
          </div>

          <div className="bk-form__field">
            <label className="bk-form__label">{t("admin.bookingNotes")}</label>
            <textarea
              className="bk-form__input bk-form__textarea"
              value={bookingForm.notes}
              onChange={handleBookingFieldChange("notes")}
              maxLength={1500}
              rows={3}
            />
          </div>

          <div className="bk-form__actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={closeBookingModal}
              disabled={savingBooking}
            >
              {t("admin.cancel")}
            </button>
            <button type="submit" className="btn btn-filled" disabled={savingBooking}>
              {savingBooking ? (
                <span className="bk-btn-loading">
                  <span className="auth-spinner auth-spinner-white" />
                </span>
              ) : (
                t("admin.save")
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Booking Confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title={t("admin.deleteBookingConfirmTitle")}
        message={t("admin.deleteBookingConfirmDesc")}
        confirmLabel={t("admin.delete") || "Delete"}
        cancelLabel={t("admin.cancel") || "Cancel"}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        type="danger"
      />

      {/* Floating scroll-to-top */}
      <button
        className={`admin-go-to-top ${showScrollTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Go to top"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default BookingsPage;
